'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';

interface CookieConsentBannerProps {
  language?: 'de' | 'en';
}

const CookieConsentBanner = ({ language = 'en' }: CookieConsentBannerProps) => {
  const [showBanner, setShowBanner] = useState(false);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // Ensure we're on the client side
    setIsClient(true);
    
    // Use a versioned key to bypass any old localStorage values
    const consentKey = 'finvion_cookie_consent_v2';
    const hasConsented = localStorage.getItem(consentKey);
    
    // Show banner if no consent recorded (null check only)
    if (hasConsented === null) {
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('finvion_cookie_consent_v2', 'accepted');
    localStorage.setItem('finvion_consent_date_v2', new Date().toISOString());
    setShowBanner(false);
  };

  const handleReject = () => {
    localStorage.setItem('finvion_cookie_consent_v2', 'rejected');
    localStorage.setItem('finvion_consent_date_v2', new Date().toISOString());
    setShowBanner(false);
  };

  // Don't render on server or if banner shouldn't show
  if (!isClient || !showBanner) return null;

  const content = {
    de: {
      title: 'Cookie-Einstellungen',
      description: 'Wir verwenden Cookies und ähnliche Technologien, um Ihnen das beste Erlebnis auf unserer Website zu bieten.',
      detailsText: 'Durch Klicken auf "Alle akzeptieren" stimmen Sie der Speicherung von Cookies auf Ihrem Gerät zu. Weitere Informationen finden Sie in unserer',
      privacyLink: 'Datenschutzerklärung',
      acceptAll: 'Alle akzeptieren',
      rejectAll: 'Alle ablehnen',
      privacyUrl: '/datenschutz'
    },
    en: {
      title: 'Cookie Settings',
      description: 'We use cookies and similar technologies to provide you with the best experience on our website.',
      detailsText: 'By clicking "Accept All" you consent to the storage of cookies on your device. For more information, please see our',
      privacyLink: 'Privacy Policy',
      acceptAll: 'Accept All',
      rejectAll: 'Reject All',
      privacyUrl: '/privacy-policy'
    }
  };

  const text = content[language];

  return (
    <div data-component-id="src/components/common/CookieConsentBanner.tsx:67:4" data-component-path="src/components/common/CookieConsentBanner.tsx" data-component-line="67" data-component-file="CookieConsentBanner.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20bottom-0%20left-0%20right-0%20bg-white%20border-t-2%20border-gray-200%20shadow-2xl%20z-%5B99999%5D%22%7D" 
      className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-2xl z-[99999]"
      role="dialog"
      aria-label={text.title}
      aria-live="polite"
    >
      <div data-component-id="src/components/common/CookieConsentBanner.tsx:73:6" data-component-path="src/components/common/CookieConsentBanner.tsx" data-component-line="73" data-component-file="CookieConsentBanner.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-6%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div data-component-id="src/components/common/CookieConsentBanner.tsx:74:8" data-component-path="src/components/common/CookieConsentBanner.tsx" data-component-line="74" data-component-file="CookieConsentBanner.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20lg%3Aflex-row%20items-start%20lg%3Aitems-center%20justify-between%20gap-4%22%7D" className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          {/* Content Section */}
          <div data-component-id="src/components/common/CookieConsentBanner.tsx:76:10" data-component-path="src/components/common/CookieConsentBanner.tsx" data-component-line="76" data-component-file="CookieConsentBanner.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20space-y-2%22%7D" className="flex-1 space-y-2">
            <h3 data-component-id="src/components/common/CookieConsentBanner.tsx:77:12" data-component-path="src/components/common/CookieConsentBanner.tsx" data-component-line="77" data-component-file="CookieConsentBanner.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-gray-900%22%2C%22textContent%22%3A%22%F0%9F%8D%AA%22%7D" className="text-lg font-semibold text-gray-900">
              🍪 {text.title}
            </h3>
            <p data-component-id="src/components/common/CookieConsentBanner.tsx:80:12" data-component-path="src/components/common/CookieConsentBanner.tsx" data-component-line="80" data-component-file="CookieConsentBanner.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-gray-600%20leading-relaxed%22%7D" className="text-sm text-gray-600 leading-relaxed">
              {text.description}
            </p>
            <p data-component-id="src/components/common/CookieConsentBanner.tsx:83:12" data-component-path="src/components/common/CookieConsentBanner.tsx" data-component-line="83" data-component-file="CookieConsentBanner.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-gray-500%22%2C%22textContent%22%3A%22.%22%7D" className="text-xs text-gray-500">
              {text.detailsText}{' '}
              <Link data-component-id="src/components/common/CookieConsentBanner.tsx:85:14" data-component-path="src/components/common/CookieConsentBanner.tsx" data-component-line="85" data-component-file="CookieConsentBanner.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22text-blue-600%20hover%3Atext-blue-800%20underline%20font-medium%22%7D" 
                href={text.privacyUrl}
                className="text-blue-600 hover:text-blue-800 underline font-medium"
              >
                {text.privacyLink}
              </Link>
              .
            </p>
          </div>

          {/* Action Buttons */}
          <div data-component-id="src/components/common/CookieConsentBanner.tsx:96:10" data-component-path="src/components/common/CookieConsentBanner.tsx" data-component-line="96" data-component-file="CookieConsentBanner.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20sm%3Aflex-row%20gap-3%20w-full%20lg%3Aw-auto%22%7D" className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
            <button data-component-id="src/components/common/CookieConsentBanner.tsx:97:12" data-component-path="src/components/common/CookieConsentBanner.tsx" data-component-line="97" data-component-file="CookieConsentBanner.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-3%20text-sm%20font-medium%20text-gray-700%20bg-gray-100%20hover%3Abg-gray-200%20rounded-lg%20transition-colors%20duration-200%20border%20border-gray-300%22%7D"
              onClick={handleReject}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 border border-gray-300"
              aria-label={text.rejectAll}
            >
              {text.rejectAll}
            </button>
            <button data-component-id="src/components/common/CookieConsentBanner.tsx:104:12" data-component-path="src/components/common/CookieConsentBanner.tsx" data-component-line="104" data-component-file="CookieConsentBanner.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-3%20text-sm%20font-medium%20text-white%20bg-blue-600%20hover%3Abg-blue-700%20rounded-lg%20transition-colors%20duration-200%20shadow-md%20hover%3Ashadow-lg%22%7D"
              onClick={handleAccept}
              className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
              aria-label={text.acceptAll}
            >
              {text.acceptAll}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;